import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../../types/user';
import { profileService } from '../../services/profile';
import { authService } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui';

const getT = (t: (k: string) => string, key: string, fb: string) => {
  const x = t(key);
  return x === key ? fb : x;
};

/** Shown on /profile/setup when the user (e.g. Google OAuth) has not chosen a role yet. */
export function InlineRoleSelection() {
  const { t } = useTranslation();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (role: UserRole) => {
    setLoading(true);
    setError(null);
    try {
      const response = await profileService.updateUserRole(role);
      authService.setToken(response.token, response.userId, response.roleId);
      checkAuth();
    } catch {
      setError(getT(t, 'profile.roleUpdateFailed', 'Failed to set your role. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-card">
      <h1 className="text-2xl font-bold text-stitch-fg">
        {getT(t, 'auth.roleSelection', 'Choose your role')}
      </h1>
      <p className="mt-2 text-stitch-muted">
        {getT(
          t,
          'auth.roleSelectionDescription',
          'Select how you plan to use InterviewR so we can tailor the experience.',
        )}
      </p>

      {error && (
        <p className="mt-4 rounded-stitch-lg bg-stitch-danger-soft px-4 py-2 text-sm text-stitch-danger" role="alert">
          {error}
        </p>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <Button
          type="button"
          disabled={loading}
          variant="secondary"
          className="h-auto flex-col items-start gap-2 p-6 text-left"
          onClick={() => pick(UserRole.Interviewee)}
        >
          <span className="text-lg font-semibold text-stitch-fg">
            {getT(t, 'auth.interviewee', 'Interviewee')}
          </span>
          <span className="text-sm font-normal text-stitch-muted">
            {getT(t, 'auth.intervieweeDesc', 'Practice interviews and get feedback.')}
          </span>
        </Button>
        <Button
          type="button"
          disabled={loading}
          variant="secondary"
          className="h-auto flex-col items-start gap-2 p-6 text-left"
          onClick={() => pick(UserRole.Interviewer)}
        >
          <span className="text-lg font-semibold text-stitch-fg">
            {getT(t, 'auth.interviewer', 'Interviewer')}
          </span>
          <span className="text-sm font-normal text-stitch-muted">
            {getT(t, 'auth.interviewerDesc', 'Conduct mock interviews for candidates.')}
          </span>
        </Button>
      </div>
    </div>
  );
}
